// import node module libraries
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';

// import custom components
// import bootstrap icons
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import { useEffect, useState } from 'react';
import EleveService from '../../services/EleveService';
import ParentService from '../../services/ParentService';

const OffcanvasAddParentForm = ({ onClick }) => {
	const [eleveOptions, setEleveOptions] = useState([]);
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [selectedEleves, setSelectedEleves] = useState(null);

	useEffect(() => {
		const fetchEleveOptions = async () => {
			try {
				const eleves = await EleveService.getEleveOptions();
				setEleveOptions(eleves);
			} catch (error) {
				console.error('Error fetching students options', error);
			}
		};

		fetchEleveOptions();
	}, []);

	const ElEVE_CHOICES = eleveOptions;

	const RELATION_CHOICES = [
		{ value: 'pere', label: 'Père' },
		{ value: 'mere', label: 'Mère' },
		{ value: 'tuteur', label: 'Tuteur' }
	];

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const parentData = new FormData();

				// Add all your form data to the FormData
				parentData.append('nom', form.nom.value);
				parentData.append('prenom', form.prenom.value);
				parentData.append('relation', form.relation.value);
				parentData.append('adresse', form.adresse.value);
				parentData.append('telephone', form.telephone.value);
				parentData.append('email', form.email.value);
				parentData.append('profession', form.profession.value);
				selectedEleves.forEach((eleve) =>
					parentData.append('eleves', eleve.value)
				);

				// Call the ParentService to create the parent
				const response = await ParentService.createParent(parentData);

				// Reset the form and state variables after successful creation
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Redirect the user to the list of parents
				window.location.href = '/education/parents';
			} catch (error) {
				console.error('Erreur lors de la création du parent', error);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<Form
			className="mb-3"
			noValidate
			validated={validated}
			onSubmit={handleSubmit}
		>
			{showAlert && (
				<Alert variant="danger">
					<ExclamationTriangleFill size={25} className="me-1" />
					{alertMessage}
				</Alert>
			)}
			<Row>
				<Col xs={12} className="mb-3">
					{/* Nom et prenom  */}
					<Form.Group>
						<Row>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="nom">
									Nom <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									name="nom"
									type="text"
									placeholder="Saisir le nom du parent"
									id="nom"
									required
									isInvalid={!!feedbackMessages['nom']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											nom: e.target.value ? '' : 'Le nom est requis.'
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['nom']}
								</Form.Control.Feedback>
							</Col>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="prenom">
									Prénom <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									name="prenom"
									type="text"
									placeholder="Saisir le prénom du parent"
									id="prenom"
									required
									isInvalid={!!feedbackMessages['prenom']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											prenom: e.target.value ? '' : 'Le prénom est requis.'
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['prenom']}
								</Form.Control.Feedback>
							</Col>
						</Row>
					</Form.Group>
				</Col>

				{/* Relation */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="relation">
							Relation <span className="text-danger">*</span>
						</Form.Label>
						<Form.Select
							name="relation"
							required
							isInvalid={!!feedbackMessages['relation']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									relation: e.target.value
										? ''
										: "La relation du parent avec l'élève est requise."
								}));
							}}
						>
							{RELATION_CHOICES.map((option, index) => (
								<option key={index} value={option.value}>
									{option.label}
								</option>
							))}
						</Form.Select>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['relation']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Adresse */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="adresse">
							Adresse <span className="text-danger">*</span>
						</Form.Label>
						<Form.Control
							name="adresse"
							type="text"
							placeholder="Saisir le l'adresse du parent"
							id="adresse"
							required
							isInvalid={!!feedbackMessages['adresse']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									adresse: e.target.value ? '' : "L'adresse est requise."
								}));
							}}
						/>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['adresse']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				<Col xs={12} className="mb-3">
					{/* Téléphone et email  */}
					<Form.Group>
						<Row>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="telephone">
									Téléphone <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									name="telephone"
									type="number"
									placeholder="Saisir le téléphone du parent"
									id="telephone"
									required
									isInvalid={!!feedbackMessages['telephone']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											telephone: e.target.value
												? ''
												: 'Le numéro de téléphone est requis.'
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['telephone']}
								</Form.Control.Feedback>
							</Col>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="email">Email</Form.Label>
								<Form.Control
									name="email"
									type="email"
									placeholder="nom@example.com"
									id="email"
									isInvalid={!!feedbackMessages['email']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											email: e.target.value
												? ''
												: "L'adresse saisie n'est pas valide."
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['email']}
								</Form.Control.Feedback>
							</Col>
						</Row>
					</Form.Group>
				</Col>

				{/* Adresse */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="profession">Profession</Form.Label>
						<Form.Control
							name="profession"
							type="text"
							placeholder="Saisir la profession du parent"
							id="profession"
							isInvalid={!!feedbackMessages['profession']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									profession: e.target.value ? '' : ''
								}));
							}}
						/>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['profession']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Multi selection */}
				<Col xs={12} className="mb-4">
					<Form.Label htmlFor="eleves">
						Enfants <span className="text-danger">*</span>
					</Form.Label>
					<Select
						isMulti
						placeholder="Selectionner les enfants de ce parent"
						options={ElEVE_CHOICES}
						name="eleves"
						className="basic-multi-select"
						classNamePrefix="select"
						required
						isInvalid={!!feedbackMessages['eleves']}
						onChange={(selectedOptions) => {
							setSelectedEleves(selectedOptions);

							setFeedbackMessages((prevMessages) => ({
								...prevMessages,
								eleves:
									selectedOptions.length > 0
										? ''
										: 'Selectionner au minimum 1 élève pour ce parent.'
							}));
						}}
					/>
				</Col>

				{/* Buttons */}
				<Col xs={12} className="mt-4">
					<Button variant="success" type="submit">
						Enregistrer
					</Button>
					<Button onClick={onClick} variant="outline-danger" className="ms-2">
						Fermer
					</Button>
				</Col>
			</Row>
		</Form>
	);
};

export default OffcanvasAddParentForm;
